import React, { FC } from 'react';
import { Modal } from '../Modal';
import { Addon, ShopContent } from '../../../types/cms_shared_types';
import { Button } from '../../Common/Button';
import { Conditional } from '../../Common/Conditional';
import { NODE_UL, render } from 'storyblok-rich-text-react-renderer';

interface AddonDetailsModalProps {
  addon: Addon;
  isSelected: boolean;
  showModal: boolean;
  onAdd: () => void;
  onSwitchTo?: () => void;
  onRemove: () => void;
  onConfirm: () => void;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  cmsShopContent: ShopContent;
  requiresSwitch?: boolean;
}

export const AddonDetailsModal: FC<AddonDetailsModalProps> = ({
  addon,
  isSelected,
  showModal,
  onAdd,
  onSwitchTo,
  onRemove,
  onConfirm,
  setShowModal,
  cmsShopContent,
  requiresSwitch,
}) => {
  const Actions: FC = () => {
    if (isSelected) {
      return (
        <div className="flex justify-between">
          <Button
            textColor="text-sami-red"
            text={
              addon.addonDetails.shortName
                ? `${cmsShopContent.addonsStep.removeAddonButton} ${addon.addonDetails.shortName}`
                : cmsShopContent.addonsStep.removeAddonButton
            }
            onClick={onRemove}
          />
          <Button filled text={cmsShopContent.addonsStep.confirmButton} onClick={onConfirm} />
        </div>
      );
    }

    if (requiresSwitch && onSwitchTo) {
      return (
        <div className="flex justify-center">
          <Button
            filled
            text={
              addon.addonDetails.shortName
                ? `${cmsShopContent.addonsStep.switchToAddonButton} ${addon.addonDetails.shortName}`
                : cmsShopContent.addonsStep.switchToAddonButton
            }
            onClick={onSwitchTo}
          />
        </div>
      );
    }

    return (
      <div className="flex justify-center">
        <Button
          filled
          text={
            addon.addonDetails.shortName
              ? `${cmsShopContent.addonsStep.addAddonButton} ${addon.addonDetails.shortName}`
              : cmsShopContent.addonsStep.addAddonButton
          }
          onClick={onAdd}
        />
      </div>
    );
  };

  return (
    <Modal
      showModal={showModal}
      setShowModal={setShowModal}
      header={
        <div className="flex flex-row items-center gap-6">
          <img className="block w-10" src={addon.addonProvider.imageUrl} />
          <div className="flex flex-col font-bold sm:gap-3 sm:flex-row sm:text-[28px]">
            <div className="text-text-dark">{addon.name}</div>
            {addon.monthlyPriceExclVat && (
              <div className="text-text-gray">
                {addon.monthlyPriceExclVat} {cmsShopContent.priceWithMonthFormat}
              </div>
            )}
          </div>
        </div>
      }
      description={
        <div className="flex flex-col gap-6 sm:gap-0">
          <div className="flex flex-row items-center w-full gap-3 pt-3 sm:gap-5">
            <div className="flex justify-center sm:w-[600px]">
              <img
                src={addon.addonDetails.imageUrl}
                className="object-contain max-h-[110px] sm:max-h-[140px] w-auto h-auto"
              />
            </div>

            <div className="flex flex-col items-start gap-2 sm:w">
              <div className="font-bold sm:text-xl">{addon.addonDetails.title}</div>
              <div className="hidden font-semibold sm:flex">{addon.addonDetails.preamble}</div>
            </div>
          </div>
          <div className="text-sm font-semibold sm:hidden">{addon.addonDetails.preamble}</div>
        </div>
      }
      outsideClickCloses
      content={
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3 px-2 text-sm sm:text-base">
            {render(addon.addonDetails.description, {
              nodeResolvers: {
                [NODE_UL]: (children) => <ul className="ml-5 list-disc">{children}</ul>,
              },
            })}
          </div>
          <Conditional
            condition={render(addon.addonDetails.offer)}
            trueRender={
              <div className="w-full px-6 py-4 text-sm rounded-xl bg-sami-hover sm:text-base">
                {render(addon.addonDetails.offer)}
              </div>
            }
          />
        </div>
      }
      actions={<Actions />}
    />
  );
};
